import type { Api } from "../api";
import { empty, h, label, pill } from "../dom";
import { when } from "../format";
import { documentState, Pile, pileOf, senderArg } from "../inquiries";
import { navigate } from "../nav";
import type { InboxMessage, Page } from "../types";

// Documents (inbox, documents.read): the mail the groups received and what was read from each attachment, in two piles.
// From senders Tare knows (customers, suppliers, Tare's people) is where Home's "documents to check" come from; from
// unknown senders is kept apart and quiet (0077: read only up to a daily limit, the rest kept unread), but never
// hidden, since a new customer's first PO lands there. A document opens on its own screen to confirm, read again or
// ignore.

const PILES: { pile: Pile; text: string }[] = [
  { pile: "known", text: "From senders Tare knows" }, { pile: "unknown", text: "From unknown senders" }, { pile: "all", text: "All" },
];
const DAYS = [7, 30, 90];

export async function documents(api: Api, query: URLSearchParams): Promise<HTMLElement> {
  const pile = pileOf(query.get("pile"));
  const days = DAYS.includes(Number(query.get("days"))) ? Number(query.get("days")) : 30;
  const toCheck = query.get("show") !== "all";
  const since = new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
  const page = await api.tool<Page<InboxMessage>>("inbox", {
    sender: senderArg(pile), since, limit: 200, ...(toCheck ? { status: "NEEDS_REVIEW" } : {}),
  });
  const q = (over: Record<string, string>) => `/documents?${new URLSearchParams({ pile, days: String(days), ...(toCheck ? {} : { show: "all" }), ...over })}`;
  const docs = page.rows.reduce((n, m) => n + (m.documents?.length ?? 0), 0);

  const showSelect = h("select", { id: "show" }, [["check", "Needing a look"], ["all", "Everything received"]].map(([v, t]) =>
    h("option", { value: v, selected: (v === "all") === !toCheck }, t)));
  const daysSelect = h("select", { id: "days" }, DAYS.map((d) => h("option", { value: String(d), selected: d === days }, `${d} days`)));
  const go = () => navigate(`/documents?${new URLSearchParams({ pile, days: daysSelect.value, ...(showSelect.value === "all" ? { show: "all" } : {}) })}`);
  showSelect.addEventListener("change", go);
  daysSelect.addEventListener("change", go);

  return h("div", { class: "page" },
    h("div", { class: "stack-tight" },
      h("h1", { class: "title" }, "Documents"),
      h("p", { class: "lede" }, `${page.rows.length}${page.next_cursor ? "+" : ""} ${page.rows.length === 1 ? "email" : "emails"} with ` +
        `${docs} ${docs === 1 ? "attachment" : "attachments"}${toCheck ? " needing a look" : ""} in the last ${days} days.`),
      pile === "unknown"
        ? h("p", { class: "caption" }, "Mail from senders Tare doesn't know is read only up to a daily limit; the rest is kept unread. " +
            "A new customer's first PO arrives here: open it to read it, confirm it or ignore it.")
        : null),
    h("nav", { class: "row-act", "aria-label": "Piles" }, PILES.map((p) =>
      h("a", { class: `button button-small${p.pile === pile ? " button-primary" : ""}`, href: q({ pile: p.pile }),
        "aria-current": p.pile === pile ? "page" : null }, p.text))),
    h("form", { class: "filters" },
      h("div", { class: "field" }, h("label", { for: "show" }, "Show"), showSelect),
      h("div", { class: "field" }, h("label", { for: "days" }, "Received in"), daysSelect)),
    page.rows.length
      ? h("div", { class: "panel scroll" }, h("table", { class: "tbl" },
          h("thead", null, h("tr", null, h("th", { scope: "col" }, "Received"), h("th", { scope: "col" }, "From"),
            h("th", { scope: "col" }, "Subject"), h("th", { scope: "col" }, "Attachments"))),
          h("tbody", null, page.rows.map((m) => h("tr", null,
            h("td", { class: "num nowrap" }, when(m.receivedAt)),
            h("td", null, m.from, h("div", { class: "caption" }, `to ${m.mailbox}@`, m.knownSender === false ? " · unknown sender" : "")),
            h("td", { class: "col-text" }, m.subject || h("span", { class: "muted" }, "(no subject)")),
            h("td", null, (m.documents ?? []).length
              ? h("ul", { class: "plain" }, m.documents.map((d) => {
                  const st = documentState(d);
                  return h("li", { class: "row-act" }, h("a", { class: "num", href: `/document/${d.documentId}` }, d.filename),
                    pill(st.text.toLowerCase(), st.tone), d.kind !== "OTHER" ? label(d.kind.toLowerCase().replace(/_/g, " ")) : null);
                }))
              : h("span", { class: "muted" }, "None")))))))
      : empty("Nothing here", toCheck ? "Nothing needs a look. Show everything received to see the rest." : "No mail in this period."),
    page.next_cursor ? h("p", { class: "caption" }, "More than 200: choose a shorter period.") : null);
}
